import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ClipboardList } from "lucide-react";
import { Card } from "@shared/ui/containers/Card";
import { HeroHeader } from "@shared/ui/headers/HeroHeader";
import { Skeleton } from "@shared/ui/feedback/Skeleton";
import { ErrorState } from "@shared/ui/feedback/ErrorState";
import { Button } from "@shared/ui/primitives/Button";
import { toast } from "@shared/ui/feedback/Toast";
import { pageCopy } from "@shared/lib/pageCopy";
import { fetchMyResponses, fetchResidentSurvey, submitSurveyResponse } from "./api";
import { formTypeLabel, getSurveyRouteContext } from "./routeContext";

const DATE_FMT = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "long",
  year: "numeric",
});

/**
 * Survey detail — intro screen for an available survey. "Start" hands
 * off to the server-driven flow renderer (the onboarding step engine,
 * which the Angular portal also reuses for survey fill-in).
 */
export function SurveyDetail(): JSX.Element {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const copy = pageCopy().surveys;
  const route = getSurveyRouteContext(location.pathname);

  const query = useQuery({
    queryKey: ["survey", id],
    queryFn: () => fetchResidentSurvey(id),
    enabled: !!id,
  });
  const responsesQuery = useQuery({
    queryKey: ["surveys-my"],
    queryFn: fetchMyResponses,
  });

  const existingDraft = (responsesQuery.data ?? []).find(
    (response) => response.survey_template_id === id && !response.submitted_at,
  );

  const startMutation = useMutation({
    mutationFn: () => {
      if (!query.data) throw new Error("Survey is not loaded yet.");
      if (existingDraft) return Promise.resolve(existingDraft);
      return submitSurveyResponse({
        survey_template_id: query.data.survey_template_id,
      });
    },
    onSuccess: (response) => {
      const params = new URLSearchParams({
        instance: response.flow_instance_id,
        context: "survey",
        kind: route.isEformRoute ? "eform" : "survey",
        title: query.data?.name ?? (route.isEformRoute ? "eForm" : "Survey"),
        backTo: route.listRoute,
      });
      navigate(`/onboarding?${params.toString()}`);
    },
    onError: (error) => {
      toast.danger(
        "Couldn't start survey",
        error instanceof Error ? error.message : undefined,
      );
    },
  });

  const survey = query.data;
  const formLabel = formTypeLabel(survey?.flow_type, route.isEformRoute);
  const header = (
    <HeroHeader
      eyebrow={copy.eyebrow}
      title={survey?.name ?? copy.title}
      subtitle={
        survey ? detailSubtitle(survey.deadline_at, formLabel) : copy.subtitle
      }
      backTo={route.listRoute}
      backLabel={route.detailBackLabel}
      backBehavior="parent"
    />
  );

  if (query.isLoading) {
    return (
      <div className="min-h-full bg-paper">
        {header}
        <div className="px-5 pt-5">
          <Skeleton className="h-28" />
        </div>
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <div className="min-h-full bg-paper">
        {header}
        <div className="px-5 pt-5 pb-safe-or-6">
          <ErrorState onRetry={() => query.refetch()} />
        </div>
      </div>
    );
  }

  const surveyData = query.data;
  const resolvedFormLabel = formTypeLabel(surveyData.flow_type, route.isEformRoute);
  const isClosed = surveyData.deadline_at
    ? new Date(surveyData.deadline_at).getTime() < Date.now()
    : false;

  return (
    <div className="min-h-full bg-paper">
      <HeroHeader
        eyebrow={copy.eyebrow}
        title={surveyData.name}
        subtitle={detailSubtitle(surveyData.deadline_at, resolvedFormLabel)}
        backTo={route.listRoute}
        backLabel={route.detailBackLabel}
        backBehavior="parent"
      />

      <div className="flex flex-col gap-4 px-5 pt-5 pb-safe-or-6">
        <Card noGrain className="p-5">
          <div className="flex items-start gap-3">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
              <ClipboardList className="h-6 w-6" strokeWidth={1.5} />
            </span>
            <div className="min-w-0">
              <span className="inline-flex rounded-pill bg-primary-100 px-2 py-0.5 text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-primary-700">
                {resolvedFormLabel}
              </span>
              <p className="mt-3 text-body text-ink-secondary whitespace-pre-line">
                {surveyData.description?.trim()
                  || (existingDraft
                    ? `You already started this ${resolvedFormLabel.toLowerCase()}. Continue where you left off when you're ready.`
                    : `Review the details below, then start this ${resolvedFormLabel.toLowerCase()} when you're ready.`)}
              </p>
            </div>
          </div>
        </Card>

        {surveyData.deadline_at ? (
          <Card noGrain className="p-4">
            <p className="text-caption uppercase tracking-[0.14em] text-ink-muted">
              Closing date
            </p>
            <p className="mt-1 font-display text-headline text-ink">
              {DATE_FMT.format(new Date(surveyData.deadline_at))}
            </p>
          </Card>
        ) : null}

        <Button
          width="full"
          disabled={isClosed || responsesQuery.isLoading}
          loading={startMutation.isPending}
          onClick={() => startMutation.mutate()}
        >
          {isClosed
            ? `${resolvedFormLabel} closed`
            : existingDraft
              ? `Continue ${resolvedFormLabel.toLowerCase()}`
              : `Start ${resolvedFormLabel.toLowerCase()}`}
        </Button>
      </div>
    </div>
  );
}

function detailSubtitle(
  deadlineAt: string | null | undefined,
  formLabel: string,
): string {
  if (!deadlineAt) return `${formLabel} ready to complete`;
  return `${formLabel} closes ${DATE_FMT.format(new Date(deadlineAt))}`;
}
